/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ActivatedRouteSnapshot } from '@angular/router';
import { isTruthy } from '@app/common/utils';
import { AppState, Tab, TabRouteData, Themes } from '@app/core/types';
import {
  clusterIsApacheIgnite as _clusterIsApacheIgnite,
  clusterSupportsFeature as _clusterSupportsFeature,
} from '@app/domain/cluster';
import {
  findClusterById,
  getClusterStatus,
  isClusterOnline,
  isNebulaCluster as isNebulaClusterFn,
  isTaskPrioritizationEnabled,
} from '@app/domain/clusters';
import { nodeHasAgent } from '@app/domain/node'; // https://github.com/ngrx/platform/issues/226#issuecomment-319540398
import {
  getTeamById,
  isClusterTeamOwnedByMe,
  isMeInNebulaTeam,
  isTeamOwnedByMe,
  skipPersonalAndClusterTeams,
  userFacingTeamName,
} from '@app/domain/teams';
import { userFullName } from '@app/domain/user';
import { MONITORING_DASHBOARD_GROUP_NAME } from '@app/monitoring-dashboard/constants/dashboard-id';
import { humanCompare } from '@common/utils/human-compare';
import { getRouterSelectors } from '@ngrx/router-store';
import { Action, createFeatureSelector, createSelector } from '@ngrx/store';
import { assertNever } from '@shared/domain/assert';
import { isCluster2x, isCluster3x } from '@shared/domain/cluster';
import { AuthConfig } from '@shared/types/auth';
import { ACTIVE_STATUSES_3x, ClusterFeatures, ClusterId, ClusterInfo, ClusterStatus } from '@shared/types/cluster';
import { GroupedCacheNames } from '@shared/types/common';
import { NodeConsistentId } from '@shared/types/node';
import { Team, TeamId, TeamType } from '@shared/types/team';
import { entries, groupBy, isEmpty, memoize, pick, uniq, uniqBy, values } from 'lodash-es';
import md5 from 'md5';

export const routerState = createSelector(
  (state: AppState) => state.router,
  (value) => value,
);
export const {
  selectQueryParams, // select the current route query params
  selectQueryParam, // factory function to select a query param
  selectRouteParams, // select the current route params
  selectRouteParam, // factory function to select a route param
  selectRouteData, // select the current route data
  selectUrl, // select the current url
} = getRouterSelectors(routerState);
export const dashboards = createFeatureSelector<AppState['dashboards']>('dashboards');
export const dashboardGroups = createFeatureSelector<AppState['dashboardGroups']>('dashboardGroups');
export const hasDashboardGroup = (dashboardGroupName: string) =>
  createSelector(dashboardGroups, (groups) => !!groups[dashboardGroupName]);
export const dashboardGroupByName = (dashboardGroupName: string) =>
  createSelector(dashboardGroups, (groups) => groups[dashboardGroupName]);
export const dashboardsByGroupId = (dashboardGroupName: string) =>
  createSelector(
    dashboards,
    dashboardGroups,
    (dashboards, groups) => groups[dashboardGroupName]?.workspaceIds.map((id) => dashboards[id]).filter(isTruthy) ?? [],
  );
export const dashboardGroupIdByName = (dashboardGroupName: string) =>
  createSelector(dashboardGroups, (groups) => groups[dashboardGroupName]?.id);
export const currentDashboardGroupName = createSelector(routerState, () => MONITORING_DASHBOARD_GROUP_NAME);
export const dashboardById = memoize(
  (dashboardGroupName: string, dashboardId: string) =>
    createSelector(dashboardsByGroupId(dashboardGroupName), (dashboardGroup) =>
      dashboardGroup.find((db) => db?.id === dashboardId),
    ),
  (group, id) => JSON.stringify({ group, id }),
);

export const dashboardGroupLoaded = memoize((dashboardGroupName: string) =>
  createSelector(
    dashboardGroupByName(dashboardGroupName),
    dashboards,
    (group, dashboards) => !!group && group.workspaceIds.every((id) => !!dashboards[id]),
  ),
);
const findTab = (state: ActivatedRouteSnapshot): string =>
  (state.data as TabRouteData).isTab && state.url[0]?.path
    ? state.url[0].path
    : state.firstChild! && findTab(state.firstChild!);

export const currentTab = createSelector(routerState, (state) => (state?.state ? findTab(state.state.root) : null));
export const dashboardTabs = memoize((dashboardGroupName: string) =>
  createSelector(dashboardsByGroupId(dashboardGroupName), (dashboards) =>
    dashboards.map(
      (db): Tab => ({
        id: db.id,
        name: db.metadata.title,
        required: dashboards.length === 1,
      }),
    ),
  ),
);

export const currentDashboard = createSelector(
  dashboards,
  dashboardGroups,
  currentDashboardGroupName,
  currentTab,
  (state, groupsState, groupId, dashboardId) => {
    if (dashboardId && dashboardId !== 'default') {
      return state[dashboardId] ? state[dashboardId] : undefined;
    }
    const group = groupsState[groupId];
    const firstDashboard = group ? group.workspaceIds[0] : undefined;
    return firstDashboard ? state[firstDashboard] : undefined;
  },
);
export const currentDashboardChartRange = createSelector(currentDashboard, (db) =>
  db ? (db.metadata.chartZoomRange ?? db.metadata.chartsRange) : undefined,
);

export const closedBanners = createFeatureSelector<AppState['closedBanners']>('closedBanners');
export const isBannerVisible = (id: string) =>
  createSelector(closedBanners, (closedBanners) => !closedBanners.includes(id));

export const currentClusterSelector = createFeatureSelector<AppState['currentCluster']>('currentCluster');
export const clusterList = createFeatureSelector<AppState['clusters']>('clusters');
export const clustersLoaded = createFeatureSelector<AppState['clustersLoaded']>('clustersLoaded');
export const hasClusters = createSelector(clustersLoaded, clusterList, (loaded, clusters) =>
  loaded ? !!clusters.length : null,
);
export const currentCluster = createSelector(currentClusterSelector, clustersLoaded, (currentCluster, loaded) =>
  loaded ? currentCluster : null,
);
export const currentClusterInfo = createSelector(currentCluster, clusterList, (clusterId, clusters) =>
  clusterId ? findClusterById(clusters, clusterId) : undefined,
);
export const currentClusterEdition = createSelector(currentClusterInfo, (clusterInfo) => clusterInfo?.edition);
export const currentClusterName = createSelector(currentClusterInfo, (clusterInfo) => clusterInfo?.tag);
export const currentClusterId = createSelector(currentClusterInfo, (clusterInfo) => clusterInfo?.id);
export const currentClusterHighestVersion = createSelector(
  currentClusterInfo,
  (clusterInfo) => clusterInfo?.versions[0],
);
export const currentClusterStatusSelector = createSelector(currentClusterInfo, getClusterStatus);
export const isCurrentClusterinStatus = memoize((status: ClusterStatus) =>
  createSelector(currentClusterStatusSelector, (value) => value === status),
);
export const isCurrentClusterActive = createSelector(currentClusterInfo, (cluster) =>
  cluster && isCluster2x(cluster) ? cluster.connected && cluster.info.active : undefined,
);
export const isCurrentClusterInactive = createSelector(currentClusterInfo, (cluster) =>
  cluster && isCluster2x(cluster) ? cluster.connected && !cluster.info.active : undefined,
);
export const isCurrentClusterUninitialized = createSelector(currentClusterInfo, (cluster) =>
  cluster && isCluster3x(cluster) ? cluster.status === ClusterStatus.UNINITIALIZED : undefined,
);
export const isCurrentClusterInitialized = createSelector(currentClusterInfo, (cluster) =>
  cluster && isCluster3x(cluster) ? ACTIVE_STATUSES_3x.includes(cluster.status) : undefined,
);
export const isCurrentClusterOnline = createSelector(currentClusterInfo, (cluster) =>
  cluster ? isClusterOnline(cluster) : undefined,
);
export const isNebulaCluster = createSelector(currentClusterInfo, (cluster) =>
  cluster ? isNebulaClusterFn(cluster) : false,
);
export const isAttachedCluster = createSelector(currentClusterInfo, (cluster) =>
  cluster ? !isNebulaClusterFn(cluster) : false,
);
export const isCurrentClusterTaskPrioritizationEnabled = createSelector(currentClusterInfo, (cluster) =>
  cluster ? isTaskPrioritizationEnabled(cluster) : false,
);

export const canCurrentClusterRunActions = createSelector(currentClusterInfo, (cluster) => {
  const status = cluster?.status;
  switch (status) {
    case ClusterStatus.PROVISIONING:
    case ClusterStatus.RESUMING:
    case ClusterStatus.DESTROYING:
    case ClusterStatus.DESTROYED:
    case ClusterStatus.FAILED:
    case ClusterStatus.INACTIVE:
    case ClusterStatus.DISCONNECTED:
    case ClusterStatus.SUSPENDED:
    case ClusterStatus.SUSPENDING:
    case ClusterStatus.UNINITIALIZED:
    case undefined:
      return false;

    case ClusterStatus.ACTIVE:
    case ClusterStatus.LIMITED:
      return true;

    default:
      return assertNever(status);
  }
});
export const clusterTopologies = createFeatureSelector<AppState['clusterTopology']>('clusterTopology');
export const clusterTopologyByClusterId = (clusterId: string) =>
  createSelector(clusterTopologies, (state) => state.get(clusterId));
export const clusterTopologyLoaded = (clusterId: string) =>
  createSelector(clusterTopologies, (state) => state.has(clusterId));
export const clusterCoordinator = (clusterId: string) =>
  createSelector(
    clusterTopologyByClusterId(clusterId),
    (topology) => topology && topology.nodes.find((node) => node.consistentId === topology.coordinatorConsistentId),
  );
export const clusterById = memoize((clusterId: string) =>
  createSelector(clusterList, (clusters) => findClusterById(clusters, clusterId)),
);

export const clusterTag = memoize((clusterId: ClusterId) =>
  createSelector(clusterList, (clusters: AppState['clusters']) =>
    clusters ? findClusterById(clusters, clusterId)?.tag || 'Unknown Cluster' : null,
  ),
);

export const currentClusterTopology = createSelector(currentCluster, clusterTopologies, (clusterId, topologies) =>
  clusterId ? topologies.get(clusterId) : undefined,
);
export const currentClusterNodes = createSelector(currentClusterTopology, (topology) =>
  topology ? topology.nodes : [],
);

export const isCurrentClusterNodeOnline = (nodeConsistentId: NodeConsistentId) =>
  createSelector(currentClusterNodes, (nodes) =>
    !isEmpty(nodes) ? nodes.find((n) => n.consistentId === nodeConsistentId)?.online || false : false,
  );

export const currentClusterNodesWithNoAgent = createSelector(currentClusterNodes, (nodes) =>
  nodes.filter((node) => !nodeHasAgent(node)),
);
export const isCurrentClusterNodesMissingAgent = createSelector(
  currentClusterNodesWithNoAgent,
  (nodes) => !!nodes.length,
);
export const currentClusterNodeIdMapping = createSelector(currentClusterNodes, (nodes) => ({
  nodeIdToConsistentId: Object.fromEntries(nodes.map((n) => [n.nodeId, n.consistentId])),
  consistentIdToNodeId: Object.fromEntries(nodes.map((n) => [n.consistentId, n.nodeId])),
  consistentIdHashToNodeId: Object.fromEntries(nodes.map((n) => [md5(n.consistentId), n.nodeId])),
}));
export const nodeIdForConsistentIdHash = (consistentIdHash: string) =>
  createSelector(currentClusterNodeIdMapping, (mapping) => mapping.consistentIdHashToNodeId[consistentIdHash]);
export const isAuthorized = createSelector(
  (state: AppState) => state.user,
  (u) => !!u,
);
export const loadingState = createFeatureSelector<AppState['loading']>('loading');
export const isLoading = createSelector(loadingState, (loading) => !!loading.size);
export const user = createFeatureSelector<AppState['user']>('user');
export const userCountry = createSelector(user, (user) => user?.country);
export const connectionTokenSelector = createFeatureSelector<AppState['connectionToken']>('connectionToken');
export const routeClusterId = selectRouteParam('clusterId');
export const routeParamsTeamId = selectRouteParam('teamId');
export const queryParamsTeamId = selectQueryParam('teamId');
export const blockedNotifications = createFeatureSelector<AppState['blockNotifications']>('blockNotifications');
export const theme = createFeatureSelector<AppState['theme']>('theme');
export const isDarkTheme = createSelector(theme, (theme) => theme === Themes.DARK);
export const clusterSupportsFeature = memoize(
  (clusterId: string, feature: ClusterFeatures) =>
    createSelector(clusterById(clusterId), (clusterInfo) =>
      !clusterInfo ? false : _clusterSupportsFeature(clusterInfo, feature),
    ),
  (...args) => values(args).join('_'),
);

export const currentClusterSupportsFeature = memoize((feature: ClusterFeatures) =>
  createSelector(currentClusterInfo, (clusterInfo: ClusterInfo | undefined) =>
    !clusterInfo ? false : _clusterSupportsFeature(clusterInfo, feature),
  ),
);
export const currentClusterIsApacheIgnite = createSelector(
  currentClusterInfo,
  (clusterInfo: ClusterInfo | undefined) => (clusterInfo ? _clusterIsApacheIgnite(clusterInfo) : null),
);

export const cachesSelector = createFeatureSelector<AppState['caches']>('caches');
export const cachesByClusterId = (clusterId: string) =>
  createSelector(cachesSelector, (caches) => caches.get(clusterId));
export const currentClusterCaches = createSelector(currentCluster, cachesSelector, (clusterId, caches) =>
  clusterId ? caches.get(clusterId) : null,
);
export const currentClusterNotSystemCaches = createSelector(
  currentClusterCaches,
  (caches) => caches?.filter((c) => !c.systemCache) || null,
);
export const currentClusterNotSystemCacheById = (cacheId: string) =>
  createSelector(currentClusterNotSystemCaches, (caches) => caches?.find((c) => c.cacheId === cacheId) || null);
export const currentClusterNotSystemCacheByName = (cacheName: string) =>
  createSelector(currentClusterNotSystemCaches, (caches) => caches?.find((c) => c.name === cacheName) ?? null);

export const clusterIdsOfLoadedCaches = createSelector(cachesSelector, (caches) => [...caches.keys()]);
export const currentClusterNotSystemCacheNames = createSelector(currentClusterNotSystemCaches, (caches) =>
  caches ? caches.map((c) => c.name) : null,
);
export const currentClusterNotSystemCacheGroups = createSelector(currentClusterNotSystemCaches, (caches) =>
  uniq(caches?.map((c) => c.group).filter(isTruthy)),
);
export const currentClusterNotSystemCacheGroupsWithCacheNames = createSelector(
  currentClusterNotSystemCaches,
  (caches): GroupedCacheNames[] | null => {
    if (!caches) return null;
    // groupBy converts undefined to a string value,so we need to use determined name for that category
    const ungroupedCacheNames = 'ungrouped';
    return entries(groupBy(caches, (item) => item.group ?? ungroupedCacheNames)).map(([group, caches]) => ({
      group: group === ungroupedCacheNames ? undefined : group,
      caches: caches.map((cache) => cache.name),
    }));
  },
);

export const sortModels = createFeatureSelector<AppState['sortModels']>('sortModels');
export const sortModelById = (id: string) => createSelector(sortModels, (models) => models[id]);
export const sizeModels = createFeatureSelector<AppState['sizeModels']>('sizeModels');
export const sizeModelById = (id: string) => createSelector(sizeModels, (models) => models[id]);
export const splitAreaSizeModels = createFeatureSelector<AppState['splitAreaSizeModels']>('splitAreaSizeModels');
export const splitAreaSizeModelById = (id: string) => createSelector(splitAreaSizeModels, (models) => models[id]);
export const columnVisibilityModels =
  createFeatureSelector<AppState['columnVisibilityModels']>('columnVisibilityModels');
export const columnVisibilityModelById = (id: string) => createSelector(columnVisibilityModels, (models) => models[id]);
export const licenseStatus = createFeatureSelector<AppState['licenseStatus']>('licenseStatus');
export const licenseStatusError = createSelector(licenseStatus, (status) => status?.errorMessage);
export const isAuthPage = createSelector(selectUrl, (url) => url && url.split('/')[1] === 'auth');
const authConfig = createFeatureSelector<AppState['authConfig']>('authConfig');
export const signupEnabled = createSelector(authConfig, (config: AuthConfig | null): boolean => {
  if (config?.logpass.signin === false) {
    return false;
  } else {
    return !!config?.logpass?.signup && !config?.ldap.enabled;
  }
});
export const opedIdProviders = createSelector(authConfig, (config) => config?.oidc?.providers ?? []);
export const isNonLdap = createSelector(authConfig, (config) => !config?.ldap.enabled);
export const logpassSigninEnabledSelector = createSelector(authConfig, (config): boolean => !!config?.logpass?.signin);

export const teams = createFeatureSelector<AppState['teams']>('teams');
export const clusterTeamsThatIOwn = createSelector(teams, (teams) =>
  teams ? teams.filter((team) => isClusterTeamOwnedByMe(team)) : null,
);
export const teamsExceptClusterTeamsThatIOwn = createSelector(teams, (teams) =>
  teams ? teams.filter((team) => !isClusterTeamOwnedByMe(team)) : null,
);

export const teamsExceptPersonalAndCluster = createSelector(teams, (teams) =>
  teams ? skipPersonalAndClusterTeams(teams) : null,
);
export const team = memoize((teamId: TeamId) =>
  createSelector(teams, (teams: AppState['teams']) => (teams ? (getTeamById(teams, teamId) ?? null) : null)),
);

export const teamMembersExceptMe = createSelector(teams, (teams) =>
  teams && teams.length
    ? uniqBy(
        teams
          .flatMap((it) => it.members.filter((member) => !member.isMe))
          .map((u) => ({ name: userFullName(u), email: u.email })),
        'email',
      ).sort((a, b) => humanCompare(a.name, b.name))
    : [],
);

export const clusterManagementTeams = createSelector(teamsExceptClusterTeamsThatIOwn, (teams) =>
  teams && teams.length
    ? teams
        .slice()
        .sort((a, b) => {
          if (a.type === TeamType.personal) return -1;
          if (b.type === TeamType.personal) return 1;
          return humanCompare(a.name, b.name);
        })
        .map<Team>((team) => ({ ...team, name: userFacingTeamName(team) }))
    : null,
);

export type TeamWithClusters = Team & { clusters: ClusterInfo[] };
export const clusterListByManagementTeams = createSelector(
  clusterManagementTeams,
  clusterList,
  (teams, clusters) =>
    teams?.reduce((acc, team) => {
      const teamClusters = clusters.filter(({ teamIds }) => teamIds.includes(team.id));
      return teamClusters.length
        ? [
            ...acc,
            {
              ...team,
              clusters: teamClusters,
            },
          ]
        : acc;
    }, [] as TeamWithClusters[]) || [],
);

export const teamsIOwn = createSelector(clusterManagementTeams, (teams) =>
  teams ? teams.filter(isTeamOwnedByMe) : [],
);

export const teamsIOwnExceptPersonalAndCluster = createSelector(teamsExceptPersonalAndCluster, (teams) =>
  teams ? teams.filter(isTeamOwnedByMe) : null,
);

export const isNebulaRegularUserAccess = createSelector(isNebulaCluster, teams, (isNebulaCluster, teams) =>
  isNebulaCluster && teams ? !isMeInNebulaTeam(teams) : false,
);
export const isNebulaTeamUserAccess = createSelector(isNebulaCluster, teams, (isNebulaCluster, teams) =>
  isNebulaCluster && teams ? isMeInNebulaTeam(teams) : false,
);

export const filterPanels = createFeatureSelector<AppState['filterPanels']>('filterPanels');
export const filterPanelHasData = memoize((name: string) =>
  createSelector(filterPanels, (filterPanels: AppState['filterPanels']) => filterPanels[name]?.hasData ?? false),
);
export const filterPanelVisibility = (name: string) =>
  createSelector(filterPanels, (filterPanels: AppState['filterPanels']) => {
    const filter = filterPanels[name];
    return filter ? pick(filter, ['visible', 'type']) : { visible: true, type: 'auto' };
  });

export const isRequestInFlight = memoize((action: Action) =>
  createSelector(loadingState, (loading) => loading.has(action.type)),
);

export const targetSelector = createFeatureSelector<AppState['target']>('target');

export const currentClusterSupportsDR = createSelector(
  currentClusterSupportsFeature(ClusterFeatures.DATA_REPLICATION),
  currentClusterSupportsFeature(ClusterFeatures.DATA_REPLICATION_MONITORING),
  (a, b) => a && b,
);

export const isAdminPageOpened = createSelector(selectUrl, (url) => !!url?.match(/\/(admin)\//));
